import styled from "@emotion/styled";
import { ASSOCIATED_TOKEN_PROGRAM_ID, getAssociatedTokenAddress, TOKEN_PROGRAM_ID } from "@solana/spl-token0.3.8";
import { useConnection } from "@solana/wallet-adapter-react";
import { PublicKey } from "@solana/web3.js";
import type { StepColumnsType } from "@stepfinance/react";
import {
  CellDefault,
  CheckboxFilter,
  ContextButton,
  formatBalance,
  getColumnSearchProps,
  imageFromPlatformName,
  makeSimpleFilters,
  StepAssetBreakdownPopoverChip,
  StepCustomToolTipTitle,
  StepPopoverCellWrapper,
  StepPopoverCellWrapperWithTokenIcon,
  StepTable,
  URLIcon,
} from "@stepfinance/react";
import type { TulipVault, VaultPosition } from "@stepfinance/vaults";
import { TULIP_TOKEN_MINT } from "@stepfinance/vaults";
import type { TablePaginationConfig } from "antd";
import { Spin } from "antd";
import type { FilterValue } from "antd/lib/table/interface";
import React, { useEffect, useMemo, useState } from "react";

import { findOrCreateAccountByMint } from "../../../actions";
import { harvest } from "../../../actions/tulip";
import { ReactComponent as StakingIcon } from "../../../assets/yieldagg.svg";
import { DUST_THRESHOLD, useHideDustConfig } from "../../../contexts/app/config";
import { useEnrichedWallet } from "../../../contexts/enrichedWallet";
import { useLanguageContext } from "../../../contexts/language/language";
import { useVaults } from "../../../contexts/vaults";
import type { CollapseControlProps } from "../../../hooks/home/useCollapseControl";
import { useConnectedWallet } from "../../../hooks/wallet/useConnectedWallet";
import { getTokenByMint, getTokenBySymbol } from "../../../utils/tokenInfo";
import { handlePreSendTxnError } from "../../../utils/transactions";
import { formatAmount, isPaginationHidden, useLocalStorageState } from "../../../utils/utils";
import { isTulipVault } from "../../../utils/vaults/vaults";
import {
  IconTextContainer,
  PAGE_SIZE_OPTIONS,
  StepPopoverAssetWithIconRow,
  StepPopoverInnerContent,
  TokenIconText,
} from "../../shared";
import CellValue from "../../Table/CellValue";
import { OneOrTwoTokenIcons } from "../../TokenIcon";
import { VaultPopoverContent } from "../../Vaults/VaultPopoverContent";
import { VaultTitlePopover } from "../../Vaults/VaultTitlePopover";
import { ChevronCollapse } from "../ChevronCollapse/ChevronCollapse";

export const YieldAggGrid: React.FC<CollapseControlProps> = ({ activePanels, getPanelKey, onPanelClick }) => {
  const { hideDust } = useHideDustConfig();
  const { connection } = useConnection();
  const wallet = useConnectedWallet();
  const { watching } = useEnrichedWallet();

  const { tulipVaults, tulipStrategyVaults, katanaOptionVaults, kaminoVaults, psyFiVaults, setVaultsBalance } =
    useVaults();

  const filteredVaults = useMemo(() => {
    if (!tulipVaults) {
      return [];
    }
    return [...tulipVaults, ...tulipStrategyVaults, ...katanaOptionVaults, ...psyFiVaults, ...kaminoVaults].filter(
      (v) => {
        if (!v.userUSDValue) {
          return false;
        }
        if (hideDust) {
          return v.userUSDValue > DUST_THRESHOLD;
        }
        return v.userUSDValue > 0;
      },
    );
  }, [tulipVaults, tulipStrategyVaults, katanaOptionVaults, psyFiVaults, kaminoVaults, hideDust]);

  const [filteredInfo, setFilteredInfo] = useState<Record<string, FilterValue | null>>({});
  const [pagination, setPagination] = useState<TablePaginationConfig>({});
  const [paginationSetting, setPaginationSetting] = useLocalStorageState(
    "vaultsPaginationSizeSetting",
    PAGE_SIZE_OPTIONS.m[0],
  );

  const {
    currentLanguageData: {
      common,
      components: {
        table: { assetBreakdownPopover, rewardBreakdown },
      },
      pages: {
        dashboard: {
          modules: { vaults: vaultText },
        },
      },
    },
  } = useLanguageContext();

  useEffect(() => {
    setPagination({
      hideOnSinglePage: isPaginationHidden(filteredVaults.length, PAGE_SIZE_OPTIONS.m),
      showSizeChanger: true,
      pageSizeOptions: PAGE_SIZE_OPTIONS.m,
      pageSize: parseInt(paginationSetting),
    });
  }, [filteredVaults.length, paginationSetting]);

  const [claimingId, setClaimingId] = useState<string>("");

  const totalVaultValue = useMemo(() => {
    if (filteredVaults.length === 0) {
      return 0;
    }
    return filteredVaults.reduce(function (total, currentValue) {
      return total + ((currentValue.userUSDValue ?? 0) + (currentValue.pendingRewardUSD || 0));
    }, 0);
  }, [filteredVaults]);

  useEffect(() => {
    setVaultsBalance(totalVaultValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalVaultValue]);

  const handleClaim = async (farmInfo: TulipVault) => {
    let rewardAccount: PublicKey | undefined;

    if (wallet) {
      rewardAccount = await findOrCreateAccountByMint(
        connection,
        wallet.publicKey,
        wallet.publicKey,
        [],
        TULIP_TOKEN_MINT,
      );

      if (rewardAccount) {
        setClaimingId(farmInfo.key);

        try {
          const pdaAssociatedTulipAddress = await getAssociatedTokenAddress(
            TULIP_TOKEN_MINT,
            new PublicKey(farmInfo.pda),
            true,
            TOKEN_PROGRAM_ID,
            ASSOCIATED_TOKEN_PROGRAM_ID,
          );

          await harvest(connection, wallet, farmInfo, rewardAccount, pdaAssociatedTulipAddress);
        } catch (error) {
          handlePreSendTxnError(error as Error, "Error claiming rewards");
        } finally {
          setClaimingId("");
        }
      }
    }
  };

  const columns: StepColumnsType<VaultPosition> = [
    {
      title: common.platform,
      dataIndex: "platform",
      key: "platform",
      filters: makeSimpleFilters(filteredVaults.map((vault) => vault.platform)),
      filterDropdown: (props) => <CheckboxFilter {...props} />,
      onFilter: (value, record) => record.platform.startsWith(value.toString()),
      filteredValue: filteredInfo.platform ?? null,
      render(_: string, record: any) {
        return (
          <IconTextContainer>
            <URLIcon src={imageFromPlatformName(record.platform)} />
            <TokenIconText bold>{record.platform}</TokenIconText>
          </IconTextContainer>
        );
      },
      sorter: (a: any, b: any) => a.platform.localeCompare(b.platform),
    },
    {
      title: common.vault,
      dataIndex: "vault",
      key: "vault",
      render(_: string, record: any) {
        return (
          <StepPopoverCellWrapperWithTokenIcon>
            <OneOrTwoTokenIcons
              tokenAMint={record.vault.tokenAMint}
              tokenBMint={record.vault.tokenBMint}
              url={record.vault.url}
              name={record.vault.name}
            />
            {record.platform === "PsyFi" ? (
              <StepAssetBreakdownPopoverChip
                onlyContent={true}
                title={
                  <VaultTitlePopover
                    description={record.vault.vaultDescription}
                    isVoltage={record.vault.isVoltage}
                    isStaked={record.stakedVault?.isStaked}
                    remainingLockupDays={record.remainingLockupDays}
                  />
                }
                content={
                  <VaultPopoverContent
                    vaultTokenSymbol={record.vault.vaultToken}
                    vaultTokenPrice={record.vault.tokenPrice}
                    underlyingTokenSymbol={record.tokenSymbols[0]}
                    underlyingPrice={record.vault.underlyingPrice}
                    depositTokenSymbol={record.depositTokenSymbol}
                    strikePrice={record.vault.strikePrice}
                    expiration={record.vault.expiration}
                    optionType={record.vault.optionType}
                    vaultStrategy={record.vault.vaultStrategy}
                    amountStaked={record.stakedVault?.amountStaked}
                    rewardMint={record.stakedVault?.rewardMint}
                    rewardAPY={record.stakedVault?.apy}
                    voltVaultType={record.vault.voltType}
                  />
                }
              />
            ) : (
              record.vault?.vaultDescription && (
                <StepAssetBreakdownPopoverChip
                  onlyContent={true}
                  title={common.description}
                  content={<span>{record.vault?.vaultDescription}</span>}
                />
              )
            )}
          </StepPopoverCellWrapperWithTokenIcon>
        );
      },
      sorter: (a, b) => a.vault.name.localeCompare(b.vault.name),
      ...getColumnSearchProps((record) => record.vault.name, "vault"),
      filteredValue: filteredInfo.vault ?? null,
    },
    {
      title: "APY",
      dataIndex: "apy",
      key: "apy",
      className: "column-number",
      render(_: string, record) {
        const value = record.apy;
        const loading = Number.isNaN(value) || !Number.isFinite(value);
        return loading ? <Spin /> : <CellValue cellType="percentage">{value / 100}</CellValue>;
      },
      sorter: (a: VaultPosition, b: VaultPosition) => {
        return a.apy - b.apy;
      },
    },
    {
      title: common.pendingRewards,
      dataIndex: "pending",
      key: "pending",
      render(_, record) {
        const pendingSymbol = record.pendingRewardToken ?? "";
        const tokenInfo = getTokenBySymbol(pendingSymbol);
        const pendingBalance = formatBalance(record.pendingReward ?? 0, tokenInfo?.decimals);
        const rewardContent =
          record.pendingReward && record.pendingReward > 0 ? (
            <StepPopoverAssetWithIconRow
              tokenIconMintAddress={tokenInfo?.address}
              amount={pendingBalance}
              symbol={pendingSymbol}
            />
          ) : (
            <></>
          );

        const stakedPendingRewardUSD = 0;
        const stakingRewardContent = <></>;
        const totalPendingRewardUSD = (record.pendingRewardUSD ?? 0) + stakedPendingRewardUSD;

        return (record.pendingReward && record.pendingReward > 0) || totalPendingRewardUSD > 0 ? (
          <PendingRewardsContainer>
            {!watching && isTulipVault(record) && (
              <ContextButton loading={claimingId === record.key} onClick={() => handleClaim(record)}>
                {common.claim}
              </ContextButton>
            )}
            <StepPopoverCellWrapper className="rewards-container">
              <StepAssetBreakdownPopoverChip
                assetValue={totalPendingRewardUSD}
                assetValueDisplay={<CellValue cellType="userPositionValue">{totalPendingRewardUSD}</CellValue>}
                title={rewardBreakdown.title}
                content={
                  <>
                    {rewardContent}
                    {stakingRewardContent}
                  </>
                }
              />
            </StepPopoverCellWrapper>
          </PendingRewardsContainer>
        ) : (
          <CellDefault />
        );
      },
      className: "column-number",
      sorter: (a: any, b: any) => a.pendingRewardUSD - b.pendingRewardUSD,
    },
    {
      title: common.value,
      dataIndex: "value",
      key: "value",
      className: "column-number",
      render(_, record: any) {
        const isLP = record.tokenSymbols[1];

        const tokenAMintAddress = record.vault.tokenAMint;
        const tokenAInfo = getTokenByMint(tokenAMintAddress);
        const tokenASymbol = tokenAInfo?.symbol ?? "";
        const tokenAAmount = formatBalance(record.userLiquidityA, tokenAInfo?.decimals);

        const tokenBSymbol = record.tokenSymbols[1];
        const tokenBInfo = getTokenBySymbol(tokenBSymbol);
        const tokenBAmount = formatBalance(record.userLiquidityB, tokenBInfo?.decimals);
        const tokenBMintAddress = tokenBInfo?.address;

        let hasSubsections = false;
        const content: React.ReactNode[] = [];

        let title = assetBreakdownPopover.title;
        // Decided to make this logic specific to the Friktion. This will help when reworking these columns for
        // specific platforms. Detailed logic can be found https://step-finance.atlassian.net/browse/SF-935?focusedCommentId=11863
        if (record.platform === "PsyFi") {
          const { stakedVault } = record;
          if (record.balanceEarningYield > 0 || (stakedVault && stakedVault.isStaked)) {
            title = vaultText.vaultDeposits;
            // If balanceEarningYield exists, Vault Deposits will always be the default. This checks
            // If it'll have addition sections, if so use step-inner-content component for padding, which overrides ant-d

            let amount = record.balanceEarningYield ? record.balanceEarningYield : 0;

            if (stakedVault && stakedVault.isStaked) {
              amount += stakedVault.amountStaked * stakedVault.vaultTokenPrice;
              amount = formatAmount(amount, tokenAInfo?.decimals);
            }
            if (record.pendingWithdrawals === 0 && record.claimableUnderlying === 0) {
              content.push(
                <StepPopoverAssetWithIconRow
                  key={`${tokenAMintAddress}-1`}
                  tokenIconMintAddress={tokenAMintAddress}
                  amount={amount}
                  symbol={tokenASymbol}
                />,
              );
            } else {
              content.push(
                <StepPopoverInnerContent>
                  <StepPopoverAssetWithIconRow
                    key={`${tokenAMintAddress}-2`}
                    tokenIconMintAddress={tokenAMintAddress}
                    amount={amount}
                    symbol={tokenASymbol}
                  />
                </StepPopoverInnerContent>,
              );
            }
          }
          if (record.pendingWithdrawals > 0) {
            if (record.balanceEarningYield === 0 && record.claimableUnderlying === 0) {
              title = vaultText.pendingWithdrawals;
              content.push(
                <>
                  <StepPopoverAssetWithIconRow
                    key={`withdrawal-${tokenAMintAddress}-1`}
                    tokenIconMintAddress={tokenAMintAddress}
                    amount={record.pendingWithdrawals}
                    symbol={tokenASymbol}
                  />
                </>,
              );
            } else {
              hasSubsections = true;
              content.push(
                <>
                  <StepCustomToolTipTitle title={"Pending Withdrawals"} />
                  <StepPopoverInnerContent>
                    <StepPopoverAssetWithIconRow
                      key={`withdrawal-${tokenAMintAddress}-2`}
                      tokenIconMintAddress={tokenAMintAddress}
                      amount={record.pendingWithdrawals}
                      symbol={tokenASymbol}
                    />
                  </StepPopoverInnerContent>
                </>,
              );
            }
          }
          if (record.claimableUnderlying > 0) {
            if (record.balanceEarningYield === 0 && record.pendingWithdrawals === 0) {
              title = vaultText.claimableWithdrawals;
              content.push(
                <>
                  <StepPopoverAssetWithIconRow
                    key={`claimable-${tokenAMintAddress}-1`}
                    tokenIconMintAddress={tokenAMintAddress}
                    amount={record.claimableUnderlying}
                    symbol={tokenASymbol}
                  />
                </>,
              );
            } else {
              hasSubsections = true;
              content.push(
                <>
                  <StepCustomToolTipTitle title={vaultText.claimableWithdrawals} />
                  <StepPopoverInnerContent>
                    <StepPopoverAssetWithIconRow
                      key={`claimable-${tokenAMintAddress}-2`}
                      tokenIconMintAddress={tokenAMintAddress}
                      amount={record.claimableUnderlying}
                      symbol={tokenASymbol}
                    />
                  </StepPopoverInnerContent>
                </>,
              );
            }
          }
        } else {
          content.push(
            <StepPopoverAssetWithIconRow
              key={tokenAMintAddress}
              tokenIconMintAddress={tokenAMintAddress}
              amount={tokenAAmount}
              symbol={tokenASymbol}
            />,
          );
        }

        if (isLP) {
          content.push(
            <StepPopoverAssetWithIconRow
              key={`LP-${tokenBMintAddress}-2`}
              tokenIconMintAddress={tokenBMintAddress}
              amount={tokenBAmount}
              symbol={tokenBSymbol}
            />,
          );
        }
        return (
          <StepPopoverCellWrapper>
            <StepAssetBreakdownPopoverChip
              title={title}
              content={content}
              assetValue={record.userUSDValue}
              assetValueDisplay={<CellValue cellType="userPositionValue">{record.userUSDValue}</CellValue>}
              hasSubSections={hasSubsections}
            />
          </StepPopoverCellWrapper>
        );
      },
      sorter: (a: any, b: any) => a.userUSDValue - b.userUSDValue,
      defaultSortOrder: "descend" as any,
    },
  ];
  return filteredVaults.length > 0 ? (
    <div style={{ marginTop: 20 }}>
      <ChevronCollapse
        title={common.vaults}
        value={totalVaultValue}
        TitleIcon={StakingIcon}
        activePanels={activePanels}
        panelKey={getPanelKey("YieldAggGrid")}
        onChange={onPanelClick}
      >
        <StepTable<VaultPosition>
          columns={columns}
          dataSource={filteredVaults}
          pagination={pagination}
          onChange={(pagination, filters) => {
            setPaginationSetting(pagination.pageSize ? pagination.pageSize.toString() : PAGE_SIZE_OPTIONS.m[0]);
            setFilteredInfo(filters);
          }}
        />
      </ChevronCollapse>
    </div>
  ) : null;
};

const PendingRewardsContainer = styled.div`
  display: flex;

  .rewards-container {
    flex: 1;
    justify-content: flex-end !important;
    margin-left: 11px;
  }
`;
